/**
 * ErrorBoundary — React error boundary with Ops Center reporting
 *
 * Class component (React requirement for error boundaries).
 * Reports to OpsErrorClient directly (class components can't use hooks).
 * Auto-reload for chunk load errors (sessionStorage guard against infinite loops).
 * Plain HTML/CSS fallback UI (no UI library deps).
 */

import { Component, type ReactNode, type ErrorInfo } from 'react';
import { OpsErrorClient } from './ops-error-client';
import { getBrowserInfo, generateErrorFingerprint } from './error-fingerprint';
import type { ErrorConfig } from './types';

interface ErrorBoundaryProps {
  config: ErrorConfig;
  children: ReactNode;
  fallback?: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

const CHUNK_ERROR_KEY = 'error_boundary_chunk_reload';

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  private client: OpsErrorClient;

  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
    this.client = new OpsErrorClient(props.config);
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    const browserInfo = getBrowserInfo();
    const fingerprint = generateErrorFingerprint({
      errorMessage: error.message,
      actionAttempted: 'React Render',
      requestUrl: browserInfo.url,
    });

    // Report to Ops Center
    this.client.reportError({
      error_message: error.message,
      action_attempted: 'React Render',
      stack_trace: error.stack,
      source: 'error_boundary',
      current_route: typeof window !== 'undefined' ? window.location.pathname : undefined,
      browser_info: browserInfo,
      system_data: {
        componentStack: errorInfo.componentStack?.substring(0, 2000),
        fingerprint,
      },
    }).catch(() => {});

    // Auto-reload for chunk load errors (with infinite loop guard)
    if (this.isChunkError(error)) {
      const lastReload = sessionStorage.getItem(CHUNK_ERROR_KEY);
      const now = Date.now();
      if (!lastReload || now - parseInt(lastReload) > 30000) {
        sessionStorage.setItem(CHUNK_ERROR_KEY, String(now));
        window.location.reload();
        return;
      }
    }
  }

  private isChunkError(error: Error): boolean {
    const msg = error.message.toLowerCase();
    return msg.includes('chunk') || msg.includes('loading chunk') || msg.includes('dynamically imported module');
  }

  private handleReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) return this.props.fallback;

      return (
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          minHeight: '100vh', padding: '2rem', fontFamily: 'system-ui, sans-serif',
          backgroundColor: '#0f172a', color: '#e2e8f0',
        }}>
          <div style={{ maxWidth: '32rem', textAlign: 'center' }}>
            <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>⚠️</div>
            <h1 style={{ fontSize: '1.5rem', fontWeight: 600, marginBottom: '0.5rem' }}>
              Something went wrong
            </h1>
            <p style={{ color: '#94a3b8', marginBottom: '1.5rem', fontSize: '0.875rem' }}>
              {this.state.error?.message || 'An unexpected error occurred'}
            </p>
            <p style={{ color: '#64748b', marginBottom: '1.5rem', fontSize: '0.75rem' }}>
              This error has been automatically reported to our team.
            </p>
            <button
              onClick={this.handleReload}
              style={{
                padding: '0.5rem 1.5rem', backgroundColor: '#3b82f6', color: 'white',
                border: 'none', borderRadius: '0.375rem', cursor: 'pointer',
                fontSize: '0.875rem', fontWeight: 500,
              }}
            >
              Reload Page
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
